import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './grid-menu.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { Navigation, NavigationItem } from 'components/discovery/navigation-menu/typing/navigation-types.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'
import 'components/discovery/navigation-menu/components/navigation-menu-column/navigation-menu-column.js'
import 'components/discovery/navigation-menu/components/secondary-nav/secondary-nav.js'

initializeIconsByRole({
  'csc-grid-menu.more': { name: 'angles-down', style: 'regular' },
  'csc-grid-menu.all-category': { name: 'angle-right', style: 'light' },
})

export type LayoutView = 'grid' | 'masonry'
export type LinkPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export type AllCategoryLinkType = 'link' | 'button'

@customElement('csc-grid-menu')
export class CSCGridMenu extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  private urls = new BaseUrlController(this)

  @property({ type: Array, attribute: 'nav-items' })
  navItems: Navigation = []

  @property({ type: Number })
  columns = 4

  @property({ type: Number, attribute: 'max-children' })
  maxChildren: number = Infinity

  @property({ type: String })
  view: LayoutView = 'grid'

  @property({ type: Boolean })
  sidebar: boolean = false

  @property({ type: Boolean, attribute: 'all-category-link' })
  allCategoryLink = false

  @property({ type: String, attribute: 'all-category-link-type' })
  allCategoryLinkType: AllCategoryLinkType = 'link'

  @property({ type: String, attribute: 'all-category-link-variant' })
  allCategoryLinkVariant: string = 'primary'

  @property({ type: Boolean, attribute: 'all-products-link' })
  allProductsLinks: boolean = false

  @property({ type: String, attribute: 'link-position' })
  linkPosition: LinkPosition = 'top-left'

  @property({ type: Boolean, attribute: 'show-asset' })
  showAsset: boolean = false

  @property({ type: String, attribute: 'asset-size' })
  assetSize: string = '24'

  @property({ type: Boolean })
  dividers: boolean = false

  @property({ type: Boolean, attribute: 'show-all-products-link-on-overflow' })
  showAllProductsLinkOnOverflow: boolean = false

  @property({ type: String, attribute: 'default-image' })
  defaultImage: string = ''

  @state()
  private showMoreIndicator = false

  @state()
  private atScrollBottom = false

  @state()
  private activeSidebarItem: NavigationItem | null | undefined = null

  @state()
  private hasSlottedColumns = false

  private resizeObserver: ResizeObserver | undefined

  private scrollListener: ((e: Event) => void) | undefined

  private slotElement: HTMLSlotElement | null = null

  override firstUpdated(): void {
    if (this.sidebar && this.navItems.length > 0) {
      this.activeSidebarItem = this.navItems[0]
    }
    this.updateComplete.then(() => {
      this.checkOverflow()
      this.setupResizeObserver()
      this.setupScrollListener()

      this.slotElement = this.shadowRoot?.querySelector('slot[name="columns"]') as HTMLSlotElement
      if (this.slotElement) {
        this.slotElement.addEventListener('slotchange', this.handleSlotChange.bind(this))
        this.checkSlottedColumns()
      }
    })
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    if (changedProperties.has('navItems') ||
        changedProperties.has('columns') ||
        changedProperties.has('view')) {
      if (changedProperties.has('navItems') && this.sidebar && !this.activeSidebarItem && this.navItems.length > 0) {
        this.activeSidebarItem = this.navItems[0]
        this.updateComplete.then(() => {
          this.checkOverflow()
          this.checkScrollPosition()
        })
      } else {
        this.checkOverflow()
        this.checkScrollPosition()
      }
    }
    if (changedProperties.has('sidebar')) {
      if (this.sidebar && this.navItems.length > 0 && !this.activeSidebarItem) {
        this.activeSidebarItem = this.navItems[0]
      }
      this.updateComplete.then(() => {
        this.checkOverflow()
        this.checkScrollPosition()
      })
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    this.removeScrollListener()
    if (this.slotElement) {
      this.slotElement.removeEventListener('slotchange', this.handleSlotChange.bind(this))
    }
  }

  private checkOverflow(): void {
    const gridMenu = this.shadowRoot?.querySelector('.grid-menu')
    if (gridMenu) {
      const tolerance = 1
      this.showMoreIndicator = gridMenu.scrollHeight > (gridMenu.clientHeight + tolerance)
      if (this.showMoreIndicator) {
        this.checkScrollPosition()
      } else {
        this.atScrollBottom = true
      }
    } else {
      this.showMoreIndicator = false
      this.atScrollBottom = true
    }
  }

  private setupResizeObserver(): void {
    const gridMenu = this.shadowRoot?.querySelector('.grid-menu')
    if (gridMenu) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkOverflow()
        this.checkScrollPosition()
      })
      this.resizeObserver.observe(gridMenu)
    }
  }

  private setupScrollListener(): void {
    const gridMenu = this.shadowRoot?.querySelector('.grid-menu')
    if (gridMenu) {
      this.scrollListener = this.handleScroll.bind(this)
      gridMenu.addEventListener('scroll', this.scrollListener)
    }
  }

  private removeScrollListener(): void {
    const gridMenu = this.shadowRoot?.querySelector('.grid-menu')
    if (gridMenu && this.scrollListener) {
      gridMenu.removeEventListener('scroll', this.scrollListener)
    }
  }

  private handleScroll(event: Event): void {
    this.checkScrollPosition(event.target as HTMLElement)
  }

  private checkScrollPosition(targetElement?: HTMLElement): void {
    const gridMenu = targetElement || this.shadowRoot?.querySelector('.grid-menu')
    if (gridMenu && this.showMoreIndicator) {
      const { scrollTop, scrollHeight, clientHeight } = gridMenu
      const scrollTolerance = 5
      this.atScrollBottom = (scrollHeight - scrollTop - clientHeight) <= scrollTolerance
    } else if (!this.showMoreIndicator) {
      this.atScrollBottom = true
    }
  }

  private _handleSidebarItemSelect(event: CustomEvent<NavigationItem>): void {
    this.activeSidebarItem = event.detail
    this.updateComplete.then(() => {
      this.checkOverflow()
      this.checkScrollPosition()
    })
  }

  private handleSlotChange(): void {
    this.checkSlottedColumns()
    this.requestUpdate()
  }

  // Checking if the 'columns' slot has content
  private checkSlottedColumns(): void {
    const slotted = this.slotElement ?
      this.slotElement.assignedElements({ flatten: true }) :
      []
    this.hasSlottedColumns = slotted.length > 0
  }

  private renderAllCategoryLink(): TemplateResult | typeof nothing {
    if (!this.allCategoryLink || !this.activeSidebarItem) {
      return nothing
    }

    const linkText = t('all_products', { ns: 'csc-grid-menu', default: 'See all products in selected menu item' })
    const linkUrl = this.urls.path(`${this.activeSidebarItem.slug}/${this.activeSidebarItem.id}/`)

    if (this.allCategoryLinkType === 'link') {
      return html`
        <div class="all-category-link-wrapper ${this.linkPosition}">
          <cse-link url="${linkUrl}" variant="primary">
            <cse-icon icon-role="csc-grid-menu.all-category" slot="prefix"></cse-icon>
            ${linkText}
          </cse-link>
        </div>
      `
    } else {
      return html`
        <div class="all-category-link-wrapper ${this.linkPosition}">
          <cse-button href="${linkUrl}" variant="${this.allCategoryLinkVariant}">
            <cse-icon icon-role="csc-grid-menu.all-category" slot="prefix"></cse-icon>
            ${linkText}
          </cse-button>
        </div>
      `
    }
  }

  override render(): TemplateResult {
    const showVisuals = this.showMoreIndicator && !this.atScrollBottom
    const view = this.view === 'masonry' ? 'masonry-layout' : 'grid-layout'
    const visualClass = this.showMoreIndicator && !this.atScrollBottom ? 'show-gradient' : ''
    const gridItemsContainerClass = `grid-items-container ${view}`
    const gridMenuClass = `grid-menu ${visualClass}`

    return this.sidebar ?
      html`
        <div class="container">
          <cse-paper class="sidebar">
            <csc-navigation-menu-column
              navigation-items="${JSON.stringify(this.navItems)}"
              active-item-id="${this.activeSidebarItem?.id || null}"
              ?show-asset="${this.showAsset}"
              asset-size="${this.assetSize}"
              ?dividers="${this.dividers}"
              default-image="${this.defaultImage}"
              @item-select="${this._handleSidebarItemSelect}"
            ></csc-navigation-menu-column>
          </cse-paper>
          <div class="wrapper">
            ${this.activeSidebarItem ? html`
              <div class="${gridMenuClass}" style="--csc-grid-menu-column-count: ${this.columns};">
                ${this.linkPosition === 'top-left' || this.linkPosition === 'top-right' ? this.renderAllCategoryLink() : nothing }
                <div class="${gridItemsContainerClass}" style="--csc-grid-menu-column-count: ${this.columns};">
                  ${this.activeSidebarItem.childs?.filter(child => !child.hidden).map(child => html`
                    <csc-secondary-nav
                      .navigation="${child}"
                      max-children="${this.maxChildren}"
                      ?all-products-link="${this.allProductsLinks}"
                      ?show-all-products-link-on-overflow="${this.showAllProductsLinkOnOverflow}"
                    ></csc-secondary-nav>
                  `)}
                </div>
                ${this.linkPosition === 'bottom-left' || this.linkPosition === 'bottom-right' ? this.renderAllCategoryLink() : nothing }
              </div>
              <cse-chip class="more-indicator" ?hidden="${!showVisuals}">
                ${t('more', { ns: 'csc-grid-menu', default: 'More' })}
                <cse-icon icon-role="csc-grid-menu.more" slot="prefix"></cse-icon>
              </cse-chip>
            ` : nothing }
          </div>
        </div>
      ` :
      html`
        <div class="wrapper">
          <div class="${gridMenuClass}" style="--csc-grid-menu-column-count: ${this.columns};">
            <div class="${gridItemsContainerClass}" style="--csc-grid-menu-column-count: ${this.columns};">
              <slot name="columns" @slotchange="${this.handleSlotChange}"></slot>
              ${!this.hasSlottedColumns ? this.navItems.filter(item => !item.hidden).map(secondaryNav => html`
                <csc-secondary-nav
                  .navigation="${secondaryNav}"
                  max-children="${this.maxChildren}"
                  ?all-products-link="${this.allProductsLinks}"
                ></csc-secondary-nav>
              `)
              : nothing}
            </div>
          </div>
          <cse-chip class="more-indicator" ?hidden="${!showVisuals}">
            ${t('more', { ns: 'csc-grid-menu', default: 'More' })}
            <cse-icon icon-role="csc-grid-menu.more" slot="prefix"></cse-icon>
          </cse-chip>
        </div>
      `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-grid-menu': CSCGridMenu
  }
}
